// hooks/useRefreshToken.js

import { useContext, useCallback } from "react";
import AuthContext from "../store/auth-context";
import { axiosInstance } from "../axios";

// This guard prevents multiple refresh requests from being sent simultaneously
let ongoingRefresh = null;

export default function useRefreshToken() {
  // Get the new handler from the context
  const { setAccessToken, setCSRFToken, handleSessionExpiry } = useContext(AuthContext);

  const refresh = useCallback(async () => {
    if (ongoingRefresh) {
      return ongoingRefresh;
    }

    const isLoggedOut = localStorage.getItem("isLoggedOut") === "true";
    if (isLoggedOut) {
      // Don't attempt to refresh if we are explicitly logged out.
      return Promise.reject(new Error("User is logged out."));
    }

    // Start the refresh promise
    ongoingRefresh = (async () => {
      try {
        const response = await axiosInstance.post("auth/refresh-token/");
        const newAccessToken = response.data.access;
        const newCSRFToken = response.headers["x-csrftoken"];

        setAccessToken(newAccessToken);
        setCSRFToken(newCSRFToken);
        // Positive marker: lets PersistLogin skip the blocking refresh wait
        // for visitors who have never had a session (the refresh cookie is
        // httpOnly, so this flag is the only way the client can know).
        localStorage.setItem("hasSession", "true");

        return {
          accessToken: newAccessToken,
          csrfToken: newCSRFToken,
        };
      } catch (error) {
        // CRITICAL CHANGE: Instead of manually clearing state here,
        // call the centralized handler from the context.
        console.error("Refresh token failed. Triggering session expiry logic.", error);
        handleSessionExpiry(); 
        throw error; // Propagate the error to stop any ongoing requests
      } finally {
        // Clear the guard once the request is complete
        ongoingRefresh = null;
      }
    })();

    return ongoingRefresh;
  }, [setAccessToken, setCSRFToken, handleSessionExpiry]); // Add handleSessionExpiry to dependencies

  return refresh;
}
